import { useQueryClient } from '@tanstack/react-query'
import { onValue } from 'firebase/database'
import { useEffect, useRef } from 'react'

import { getRealtimeRef } from '@/integrations/firebase'

import { useErrandsServiceGetV1IndividualsByIndividualIdErrandsKey } from '../client/queries/common'

/**
 * Refetches the errand once a note attached to it finishes generating.
 *
 * Note generation runs in the background and rewrites the note's title and events. The errand
 * carries its own populated copy of each attached note, so the "Händelser" row would otherwise
 * keep showing the pre-generation title — usually none at all — until the page is reloaded. The
 * backend stamps `notesUpdatedAt` on the errand's realtime path when a generation settles.
 *
 * Same path as `useErrandReactionRealtime`, different key: that one watches `reactionAt` and
 * refetches the thread, this one watches `notesUpdatedAt` and refetches the errands list. Neither
 * reacts to the other's key.
 */
export const useErrandNotesRealtime = (params: { errandId?: string; individualId?: string }) => {
  const { errandId, individualId } = params
  const queryClient = useQueryClient()
  // Keyed to the errand rather than to the effect run, for the same reason as
  // `useErrandReactionRealtime` — see the note there.
  const seen = useRef<{ errandId: string; notesUpdatedAt?: string }>(undefined)

  useEffect(() => {
    if (!errandId || !individualId) return

    const ref = getRealtimeRef(`individuals/${individualId}/errands/${errandId}`)

    return onValue(ref, snapshot => {
      const notesUpdatedAt = snapshot.exists()
        ? (snapshot.val() as { notesUpdatedAt?: string }).notesUpdatedAt
        : undefined
      const previous = seen.current

      // First snapshot for this errand. The errands query has that state anyway, so adopt it as
      // the baseline rather than refetching for it.
      if (previous?.errandId !== errandId) {
        seen.current = { errandId, notesUpdatedAt }

        return
      }

      if (!notesUpdatedAt || notesUpdatedAt === previous.notesUpdatedAt) return
      seen.current = { errandId, notesUpdatedAt }

      queryClient.invalidateQueries({
        queryKey: [useErrandsServiceGetV1IndividualsByIndividualIdErrandsKey],
      })
    })
  }, [errandId, individualId, queryClient])
}
